"use client"

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { 
  User, 
  getUserData, 
  isAuthenticated,
  getAllAccounts,
  getActiveAccount,
  switchAccount as switchAccountStorage,
  removeAccount as removeAccountStorage,
  clearAllAccounts,
  saveAccount,
  clearTokens,
  clearAccountTokens,
  clearAllInactiveTokens,
  getLoginPath,
  type StoredAccount,
  type AuthTokens,
  getTokens,
  saveWorkspaceForAccount,
  getWorkspaceForAccount,
  clearWorkspaceForAccount,
  syncMediaSessionWithActiveAccount,
} from '@/lib/auth'
import {
  getWorkspaces,
  setUnauthorizedHandler as setUnauthorizedHandlerV3,
  type UserWorkspaceV3,
  type PermissionUserGroup as UserGroup,
} from '@/lib/api-v3'
import { setUnauthorizedHandler as setLegacyUnauthorizedHandler } from '@/lib/api'
import { userWorkspaceToSyntheticUserGroup } from '@/lib/workspace-permissions'
import { canViewAnalytics, canManageUsers } from '@/lib/permissions'
import { debugLog, debugError, debugWarn } from '@/lib/debug'

// Разделы, у которых первый сегмент URL не является реальной страницей —
// например «Процедуры» рендерится на /procedures/assigned и /areas, но
// самого /procedures не существует. Корень такого раздела — /areas.
const SECTION_ROOT_OVERRIDES: Record<string, string> = {
  procedures: 'areas',
}

function resolveSectionRoot(rootSection: string): string {
  return SECTION_ROOT_OVERRIDES[rootSection] || rootSection
}

interface UserContextType {
  user: User | null
  /** Текущий workspace пользователя (первый из списка API v3). */
  userWorkspace: UserWorkspaceV3 | null
  /** Синтетическая «группа» для совместимости с permissions / старыми экранами. */
  userGroup: UserGroup | null
  isLoading: boolean
  isRefreshing: boolean
  isSwitching: boolean
  isWorkspaceLoaded: boolean
  /** @deprecated используйте isWorkspaceLoaded */
  isGroupLoaded: boolean
  accounts: StoredAccount[]
  activeAccountId: string | null
  updateUser: (user: User | null) => void
  refreshUser: () => Promise<void>
  switchAccount: (accountId: string) => void
  addAccount: (user: User, tokens: AuthTokens) => Promise<void>
  removeAccount: (accountId: string) => void
  removeAllAccounts: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userWorkspace, setUserWorkspace] = useState<UserWorkspaceV3 | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [isWorkspaceLoaded, setIsWorkspaceLoaded] = useState(false)
  const [accounts, setAccounts] = useState<StoredAccount[]>([])
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)

  const userGroup = useMemo(
    () => userWorkspaceToSyntheticUserGroup(userWorkspace),
    [userWorkspace]
  )

  const redirectToLoginIfSessionMissing = () => {
    if (typeof window === 'undefined') return
    const loginPath = getLoginPath()
    if (window.location.pathname === loginPath || window.location.pathname === '/login') return

    const activeAccount = getActiveAccount()
    if (!activeAccount) return

    debugLog('user', '🔐 Нет валидного токена у активного аккаунта — редирект на логин')
    window.location.href = loginPath
  }

  const loadUserWorkspace = async (accessToken?: string, isBackground = false) => {
    try {
      // Если фоновое обновление (кэш уже подставлен) — не сбрасываем флаг загрузки,
      // чтобы меню не исчезало на время запроса
      if (!isBackground) {
        setIsWorkspaceLoaded(false)
      }
      
      const token = accessToken ?? getTokens()?.accessToken
      if (!token) {
        debugLog('user', "❌ Нет токена для загрузки workspace")
        setUserWorkspace(null)
        setIsWorkspaceLoaded(true)
        return
      }

      const workspaces = await getWorkspaces(token)
      const list = Array.isArray(workspaces) ? workspaces : []
      
      if (list.length > 0) {
        const primary = list[0]
        setUserWorkspace(primary)
        // Сохраняем в localStorage для следующей загрузки
        const account = getActiveAccount()
        if (account) {
          saveWorkspaceForAccount(account.id, primary)
        }
        debugLog('user', `🏢 Workspace: ${primary.workspace?.name || '—'}, predefinedRole: ${primary.role?.predefinedRole}`)
      } else {
        setUserWorkspace(null)
        debugLog('user', "⚠️ Нет workspace у пользователя")
      }
      
      setIsWorkspaceLoaded(true)
    } catch (error) {
      debugError('❌ Ошибка загрузки workspace:', error)
      setUserWorkspace(null)
      setIsWorkspaceLoaded(true)
    }
  }

  const loadAccounts = () => {
    try {
      const allAccounts = getAllAccounts()
      const activeAccount = getActiveAccount()
      
      // Сортируем по алфавиту (по имени пользователя)
      const sortedAccounts = allAccounts.sort((a, b) => 
        a.user.name.localeCompare(b.user.name, 'ru')
      )
      
      setAccounts(sortedAccounts)
      setActiveAccountId(activeAccount?.id || null)
      setUser(activeAccount?.user || null)
      
      debugLog('user', `📦 Загружено аккаунтов: ${allAccounts.length}`)
      if (activeAccount) {
        debugLog('user', `✅ Активный аккаунт: ${activeAccount.user.name}`)
      }

      // Загружаем workspace пользователя
      if (activeAccount) {
        // Синхронно читаем кэш — меню рендерится сразу правильно
        const cached = getWorkspaceForAccount(activeAccount.id)
        if (cached) {
          setUserWorkspace(cached)
          setIsWorkspaceLoaded(true)
          debugLog('user', `🏢 Workspace из кэша: ${cached.workspace?.name || '—'}`)
        }
        // Всё равно запускаем сетевой запрос в фоне для актуальности
        // isBackground=true когда кэш уже подставлен — не сбрасываем меню
        loadUserWorkspace(activeAccount.tokens.accessToken, !!cached)
      } else {
        setUserWorkspace(null)
        setIsWorkspaceLoaded(true)
      }
    } catch (error) {
      debugError('❌ Ошибка загрузки аккаунтов:', error)
      setAccounts([])
      setActiveAccountId(null)
      setUser(null)
      setUserWorkspace(null)
      setIsWorkspaceLoaded(true)
    }
  }

  const updateUser = (newUser: User | null) => {
    debugLog('user', "🔄 Обновление пользователя:", newUser?.name || 'null')
    setUser(newUser)
  }

  const refreshUser = async () => {
    try {
      if (!isAuthenticated()) {
        debugLog('user', "❌ Не авторизован, очищаем данные")
        setUser(null)
        return
      }

      setIsRefreshing(true)
      debugLog('user', "🔄 Принудительное обновление...")
      
      const { fetchUserData, saveUserData } = await import('@/lib/auth')
      const userData = await fetchUserData()
      
      if (userData) {
        // Явно сохраняем данные после получения
        saveUserData(userData)
        setUser(userData)
        debugLog('user', "✅ Обновление завершено:", userData.name)
        await loadUserWorkspace()
      } else {
        setUser(null)
        debugLog('user', "✅ Обновление завершено: null")
      }
      
      // Обновляем список аккаунтов
      loadAccounts()
      
    } catch (error) {
      debugError('❌ Ошибка при обновлении:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const backgroundRefreshUser = async () => {
    try {
      if (!isAuthenticated()) {
        debugLog('user', "❌ Не авторизован для фонового обновления")
        setUser(null)
        return
      }

      setIsRefreshing(true)
      debugLog('user', '🔄 Фоновое обновление...')
      
      const { fetchUserData, saveUserData } = await import('@/lib/auth')
      const userData = await fetchUserData()
      
      if (userData) {
        // Теперь явно сохраняем данные после получения
        saveUserData(userData)
        setUser(userData)
        debugLog('user', '✅ Фоновое обновление завершено:', userData.name)
        await loadUserWorkspace()
      } else {
        debugWarn('user', '⚠️ Не удалось обновить в фоне')
      }
      
      // Обновляем список аккаунтов
      loadAccounts()
      
    } catch (error) {
      debugError('❌ Ошибка фонового обновления:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSwitchAccount = async (accountId: string) => {
    debugLog('user', `🔄 Переключение на аккаунт: ${accountId}`)
    setIsSwitching(true)

    if (typeof window === "undefined") return

    const currentPath = window.location.pathname

    // Мобильная версия: очищаем токены текущего аккаунта и идём на логин
    if (currentPath.startsWith('/mobile')) {
      const currentActive = getActiveAccount()
      if (currentActive && currentActive.id !== accountId) {
        clearAccountTokens(currentActive.id)
      }
      switchAccountStorage(accountId)
      window.location.href = '/mobile/login'
      return
    }

    const success = switchAccountStorage(accountId)
    if (!success) {
      debugError('❌ Не удалось переключить аккаунт')
      setIsSwitching(false)
      return
    }

    // Десктоп: определяем корень текущего раздела
    const pathParts = currentPath.split('/').filter(Boolean)
    const rootSection = resolveSectionRoot(pathParts[0] || '')

    // Получаем workspace нового аккаунта (из кэша или с сервера)
    let newUserGroup: UserGroup | null = null
    try {
      const cachedWs = getWorkspaceForAccount(accountId)
      if (cachedWs) {
        newUserGroup = userWorkspaceToSyntheticUserGroup(cachedWs)
      } else {
        const account = getAllAccounts().find(a => a.id === accountId)
        if (account) {
          const wss = await getWorkspaces(account.tokens.accessToken)
          const list = Array.isArray(wss) ? wss : []
          if (list.length > 0) {
            newUserGroup = userWorkspaceToSyntheticUserGroup(list[0])
          }
        }
      }
    } catch {
      debugWarn('user', '⚠️ Не удалось загрузить workspace при переключении')
    }

    // Проверяем доступ к текущему разделу и определяем путь
    let redirectPath = rootSection ? `/${rootSection}` : '/areas'

    if (rootSection === 'analytics' && !canViewAnalytics(newUserGroup)) {
      redirectPath = '/areas'
      debugLog('user', '🚫 Нет доступа к аналитике → /areas')
    } else if (rootSection === 'users' && !canManageUsers(newUserGroup)) {
      redirectPath = '/areas'
      debugLog('user', '🚫 Нет доступа к пользователям → /areas')
    }

    debugLog('user', `🔀 Смена аккаунта: редирект на ${redirectPath}`)
    window.location.href = redirectPath
  }

  const handleAddAccount = async (user: User, tokens: AuthTokens) => {
    try {
      debugLog('user', `➕ Добавление аккаунта: ${user.name}`)
      
      const accountId = saveAccount(user, tokens)
      
      if (accountId) {
        // Переключаемся на новый аккаунт
        switchAccountStorage(accountId)

        let newUserGroup: UserGroup | null = null
        try {
          const wss = await getWorkspaces(tokens.accessToken)
          const list = Array.isArray(wss) ? wss : []
          newUserGroup = list.length > 0 ? userWorkspaceToSyntheticUserGroup(list[0]) : null
        } catch (error) {
          debugWarn('user', '⚠️ Не удалось загрузить workspace нового пользователя')
        }
        
        if (typeof window !== "undefined") {
          const currentPath = window.location.pathname
          const pathParts = currentPath.split('/').filter(Boolean)
          const rootSection = resolveSectionRoot(pathParts[0] || '')

          // Мобильный маршрут → очищаем токены всех остальных аккаунтов и идём на корень
          if (rootSection === 'mobile') {
            clearAllInactiveTokens()
            debugLog('user', '🔀 Мобильный маршрут → /mobile')
            window.location.href = '/mobile'
            return
          }

          // Проверяем доступ к текущему разделу
          let hasAccessToCurrentSection = true
          let redirectPath = currentPath

          if (rootSection === 'analytics' && !canViewAnalytics(newUserGroup)) {
            hasAccessToCurrentSection = false
            redirectPath = '/areas'
            debugLog('user', '🚫 Нет доступа к аналитике, редирект на инструкции')
          } else if (rootSection === 'users' && !canManageUsers(newUserGroup)) {
            hasAccessToCurrentSection = false
            redirectPath = '/areas'
            debugLog('user', '🚫 Нет доступа к пользователям, редирект на инструкции')
          }

          if (!hasAccessToCurrentSection) {
            debugLog('user', `🔀 Редирект на: ${redirectPath}`)
            window.location.href = redirectPath
            return
          }

          const sectionsWithSubpages = ['areas', 'files', 'users', 'analytics', 'casper-copilot']
          
          if (rootSection && sectionsWithSubpages.includes(rootSection) && pathParts.length > 1) {
            debugLog('user', `🔀 Редирект на корень раздела: /${rootSection}`)
            window.location.href = `/${rootSection}`
          } else {
            window.location.reload()
          }
        }
      }
    } catch (error) {
      debugError('❌ Ошибка при добавлении аккаунта:', error)
    }
  }

  const handleRemoveAccount = async (accountId: string) => {
    try {
      debugLog('user', `🗑️ Удаление аккаунта: ${accountId}`)
      
      clearWorkspaceForAccount(accountId)
      removeAccountStorage(accountId)
      
      // Проверяем, остались ли другие аккаунты
      const remainingAccounts = getAllAccounts()
      
      if (remainingAccounts.length === 0) {
        // Если это был последний аккаунт, редирект произойдет автоматически
        debugLog('user', "🚪 Удален последний аккаунт, редирект на /login")
      } else {
        const activeAccount = getActiveAccount()
        let newUserGroup: UserGroup | null = null
        
        if (activeAccount) {
          try {
            const wss = await getWorkspaces(activeAccount.tokens.accessToken)
            const list = Array.isArray(wss) ? wss : []
            newUserGroup = list.length > 0 ? userWorkspaceToSyntheticUserGroup(list[0]) : null
          } catch (error) {
            debugWarn('user', '⚠️ Не удалось загрузить workspace активного пользователя')
          }
        }

        if (typeof window !== "undefined") {
          // Получаем текущий путь
          const currentPath = window.location.pathname
          
          // Извлекаем корневой раздел (первая часть после '/')
          const pathParts = currentPath.split('/').filter(Boolean)
          const rootSection = resolveSectionRoot(pathParts[0] || '')
          
          // Проверяем доступ к текущему разделу
          let hasAccessToCurrentSection = true
          let redirectPath = currentPath

          if (rootSection === 'analytics' && !canViewAnalytics(newUserGroup)) {
            hasAccessToCurrentSection = false
            redirectPath = '/areas'
            debugLog('user', '🚫 Нет доступа к аналитике, редирект на инструкции')
          } else if (rootSection === 'users' && !canManageUsers(newUserGroup)) {
            hasAccessToCurrentSection = false
            redirectPath = '/areas'
            debugLog('user', '🚫 Нет доступа к пользователям, редирект на инструкции')
          }

          // Если нет доступа - редиректим на доступную страницу
          if (!hasAccessToCurrentSection) {
            debugLog('user', `🔀 Редирект на: ${redirectPath}`)
            window.location.href = redirectPath
            return
          }

          // Список разделов, где нужно редиректить на корень
          const sectionsWithSubpages = ['areas', 'files', 'users', 'analytics', 'casper-copilot']
          
          // Если мы в одном из разделов с подстраницами и не на корне
          if (rootSection && sectionsWithSubpages.includes(rootSection) && pathParts.length > 1) {
            debugLog('user', `🔀 Редирект на корень раздела: /${rootSection}`)
            window.location.href = `/${rootSection}`
          } else {
            // Обычный reload если мы уже на корне раздела или на главной странице
            window.location.reload()
          }
        }
      }
    } catch (error) {
      debugError('❌ Ошибка при удалении аккаунта:', error)
    }
  }

  const handleRemoveAllAccounts = () => {
    try {
      debugLog('user', "🗑️ Удаление всех аккаунтов")
      
      // Очищаем кэш workspace для всех аккаунтов
      getAllAccounts().forEach(acc => clearWorkspaceForAccount(acc.id))
      clearAllAccounts()
      
      // Редирект на страницу входа
      if (typeof window !== "undefined") {
        window.location.href = getLoginPath()
      }
    } catch (error) {
      debugError('❌ Ошибка при удалении всех аккаунтов:', error)
    }
  }

  // Глобальный 401 → сброс токена и редирект (без удаления аккаунта)
  useEffect(() => {
    const handler = () => {
      if (typeof window === 'undefined') return
      const loginPath = getLoginPath()
      if (window.location.pathname === loginPath) return

      debugLog('user', '🔒 401 Unauthorized — сброс токена и редирект')

      const account = getActiveAccount()
      if (!account) {
        window.location.href = loginPath
        return
      }

      // Очищаем только токены, чтобы аккаунт остался в списке для быстрого входа
      clearTokens()
      window.location.href = loginPath
    }

    setUnauthorizedHandlerV3(handler)
    setLegacyUnauthorizedHandler(handler)
    return () => {
      setUnauthorizedHandlerV3(null)
      setLegacyUnauthorizedHandler(null)
    }
  }, [])

  useEffect(() => {
    const loadUser = () => {
      try {
        syncMediaSessionWithActiveAccount()
        if (!isAuthenticated()) {
          debugLog('user', "❌ Не авторизован при инициализации")
          setUser(null)
          setAccounts([])
          setActiveAccountId(null)
          setIsLoading(false)
          redirectToLoginIfSessionMissing()
          return
        }

        // Загружаем все аккаунты
        loadAccounts()
        
        setIsLoading(false)
        
        // Фоновое обновление данных пользователя
        setTimeout(() => {
          backgroundRefreshUser()
        }, 100)
        
      } catch (error) {
        debugError('❌ Ошибка загрузки пользователя:', error)
        setUser(null)
        setAccounts([])
        setActiveAccountId(null)
        setIsLoading(false)
      }
    }

    loadUser()
  }, [])

  useEffect(() => {
    const handleAuthInvalidated = () => {
      debugLog('user', '🔄 Получено событие casper:auth-invalidated')
      setUser(null)
      setUserWorkspace(null)
      setIsWorkspaceLoaded(true)
      loadAccounts()
      redirectToLoginIfSessionMissing()
    }

    const handleAccountsUpdated = () => {
      debugLog('user', '🔄 Получено событие casper:accounts-updated')
      loadAccounts()
    }

    const handleStorageChange = (e: StorageEvent) => {
      // Реагируем на изменения в других вкладках
      if (e.key === 'accounts' || e.key === 'activeAccountId') {
        debugLog('user', "🔄 Синхронизация из другой вкладки")
        loadAccounts()
      }
    }

    window.addEventListener('casper:auth-invalidated', handleAuthInvalidated as EventListener)
    window.addEventListener('casper:accounts-updated', handleAccountsUpdated as EventListener)
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('casper:auth-invalidated', handleAuthInvalidated as EventListener)
      window.removeEventListener('casper:accounts-updated', handleAccountsUpdated as EventListener)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const value: UserContextType = {
    user,
    userWorkspace,
    userGroup,
    isLoading,
    isRefreshing,
    isSwitching,
    isWorkspaceLoaded,
    isGroupLoaded: isWorkspaceLoaded,
    accounts,
    activeAccountId,
    updateUser,
    refreshUser,
    switchAccount: handleSwitchAccount,
    addAccount: handleAddAccount,
    removeAccount: handleRemoveAccount,
    removeAllAccounts: handleRemoveAllAccounts,
  }

  return (
    <UserContext.Provider value={value}>
      {children}
      {isSwitching && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-[#0285ff]" />
          <p className="text-sm font-medium text-foreground">Переключение аккаунта...</p>
        </div>
      )}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export function withUser<P extends object>(
  Component: React.ComponentType<P>
) {
  const UserWrappedComponent = (props: P) => {
    return (
      <UserProvider>
        <Component {...props} />
      </UserProvider>
    )
  }

  UserWrappedComponent.displayName = `withUser(${Component.displayName || Component.name})`
  return UserWrappedComponent
}
